import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { evaluateDocMath } from '../../../utils/mathExpr';
import { clampFloatingMenuPosition } from '../utils/floatingMenuPosition';
import { useVariablePicker } from '../hooks/useVariablePicker';
import {
  ensureEditableCaretForInsert,
  tryDeleteAdjacentAtomic,
} from '../utils/atomicCaret';
import { formulaFromElement, formulaToHtml } from '../utils/formulaField';

const MATH_MENU_OPEN = 'fokus-math-menu-open';

/** Tell any open formula editor to close. Call this before opening another. */
export const announceMathMenu = () => {
  if (typeof document === 'undefined') return;
  document.dispatchEvent(new CustomEvent(MATH_MENU_OPEN));
};

/**
 * Floating formula editor for /math pills.
 * Variables render as pills; typing "{{" opens the same picker as the editor.
 */
export const MathFormulaMenu = ({
  position,
  initialExpr = '',
  resolveVariable,
  availableVariables = [],
  onCreateVariable = null,
  onSubmit,
  onClose,
}) => {
  const menuRef = useRef(null);
  const fieldRef = useRef(null);
  const [expr, setExpr] = useState(initialExpr);
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  const preview = useMemo(
    () => evaluateDocMath(expr, resolveVariable),
    [expr, resolveVariable]
  );

  const syncExpr = () => {
    const next = formulaFromElement(fieldRef.current);
    setExpr(next);
    fieldRef.current?.classList.toggle('is-empty', !String(next).trim());
    return next;
  };

  const { tryOpenFromBraceKey, variablePicker } = useVariablePicker({
    contentEditableRef: fieldRef,
    resolveVariable,
    availableVariables,
    onCreateVariable,
    commitHtml: () => {
      syncExpr();
    },
    restoreFocus: () => fieldRef.current?.focus({ preventScroll: true }),
  });

  useLayoutEffect(() => {
    const el = fieldRef.current;
    if (!el) return;
    el.innerHTML = formulaToHtml(initialExpr, resolveVariable);
    el.classList.toggle('is-empty', !String(initialExpr).trim());
    el.focus({ preventScroll: true });
    const sel = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    sel?.removeAllRanges();
    sel?.addRange(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- paint once on open
  }, []);

  useEffect(() => {
    const menu = menuRef.current;
    if (!menu) return undefined;
    const apply = () => setAdjustedPosition(clampFloatingMenuPosition(menu, position));
    apply();
    const raf = requestAnimationFrame(apply);
    const ro = new ResizeObserver(apply);
    ro.observe(menu);
    window.addEventListener('resize', apply);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', apply);
    };
  }, [position, expr, preview.state]);

  useEffect(() => {
    const onScroll = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (e.target.closest?.('.doc-variable-picker')) return;
      onClose();
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [onClose]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (e.target.closest?.('.doc-variable-picker')) return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  useEffect(() => {
    // The press that opened this menu is followed by a mousedown on the same
    // formula. Ignore that one, or the menu closes before it can be seen.
    let skipOutside = true;
    const onPointerDown = (e) => {
      if (menuRef.current?.contains(e.target)) {
        skipOutside = false;
        return;
      }
      if (e.target.closest?.('.doc-variable-picker')) return;
      if (skipOutside) {
        skipOutside = false;
        return;
      }
      onClose();
    };
    const onAnotherMenu = () => onClose({ restoreFocus: false });
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener(MATH_MENU_OPEN, onAnotherMenu);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener(MATH_MENU_OPEN, onAnotherMenu);
    };
  }, [onClose]);

  const submit = () => {
    onSubmit?.(syncExpr().trim());
  };

  return createPortal(
    <div
      ref={menuRef}
      className="doc-slash-menu doc-math-menu"
      style={{ top: adjustedPosition.y, left: adjustedPosition.x }}
    >
      <div
        ref={fieldRef}
        className="doc-mention-picker__search doc-math-menu__formula"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Math formula"
        data-placeholder="qty × 2 + 1"
        spellCheck={false}
        lang="zxx"
        autoCorrect="off"
        autoCapitalize="off"
        onInput={syncExpr}
        onBeforeInput={ensureEditableCaretForInsert}
        onPaste={(e) => {
          e.preventDefault();
          ensureEditableCaretForInsert();
          const plain = e.clipboardData?.getData('text/plain') ?? '';
          if (!plain) return;
          if (plain.includes('{{')) {
            document.execCommand('insertHTML', false, formulaToHtml(plain, resolveVariable));
          } else {
            document.execCommand('insertText', false, plain);
          }
          syncExpr();
        }}
        onKeyDown={(e) => {
          if (e.key === '{' && tryOpenFromBraceKey(e)) return;
          if (tryDeleteAdjacentAtomic(e)) {
            requestAnimationFrame(syncExpr);
            return;
          }
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
      />
      <div className="doc-math-menu__preview">
        <span
          className="doc-variable"
          data-doc-math=""
          data-var-state={preview.state}
          style={preview.color ? { '--var-color': preview.color } : undefined}
        >
          {preview.state === 'empty' ? 'math' : preview.text}
        </span>
        {preview.state === 'unresolved' && preview.tip ? (
          <span className="doc-math-menu__preview-tip">{preview.tip}</span>
        ) : null}
      </div>
      <div className="doc-mention-picker__hint">
        <span>+ − × ÷ ( )</span>
        <span>variables</span>
        <span>Enter insert</span>
      </div>
      {variablePicker}
    </div>,
    document.body
  );
};
